import React, { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { Panel, TimeAgo } from "@/components/sov";
import { useVesselStream } from "@/lib/useVesselStream";
import { Radio, Link2, Terminal, Cloud, Activity, AlertTriangle } from "lucide-react";

const ICON_FOR = {
  url: Link2,
  "shell-sentry": Terminal,
  cloud: Cloud,
};

const COLOR_FOR = {
  url: "#dcb95f",
  "shell-sentry": "#6cc88f",
  cloud: "#5b88c2",
};

const LiveStrikeFeed = () => {
  const [strikes, setStrikes] = useState([]);
  const [pulse, setPulse] = useState(false);
  const { connected, subscribe } = useVesselStream();

  // initial + polled load (fallback if WS blocked)
  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/intake/hulls?limit=12");
        setStrikes(data.hulls);
      } catch {}
    };
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  // WS hot-channel
  useEffect(() => {
    return subscribe((msg) => {
      if (msg.type === "hull") {
        setStrikes((prev) => [msg.data, ...prev].slice(0, 14));
        setPulse(true);
        setTimeout(() => setPulse(false), 900);
      }
    });
  }, [subscribe]);

  const heartbeatColor = connected ? "#6cc88f" : "#dcb95f";

  return (
    <Panel
      testid="panel-strike-feed"
      title="Live Strike Feed"
      code="§ I.E"
      action={
        <div className="flex items-center gap-3" data-testid="strike-feed-status">
          <span className="flex items-center gap-1.5">
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{
                background: heartbeatColor,
                boxShadow: `0 0 10px ${heartbeatColor}`,
                animation: connected ? "sev-pulse 1.4s infinite" : "none",
              }}
            />
            <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-ivory-2">
              {connected ? "WS Live" : "Polling 8s"}
            </span>
          </span>
          <Radio size={11} className="text-gold-300" />
          <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-ivory-3">
            {strikes.length} hulls in feed
          </span>
        </div>
      }
    >
      <div
        className={`relative scan-line transition-shadow ${pulse ? "shadow-[0_0_24px_rgba(220,185,95,0.3)]" : ""}`}
      >
        {strikes.length === 0 ? (
          <div className="p-12 text-center font-serif italic text-[14px] text-ivory-3" data-testid="strike-empty">
            No hulls yet. Issue a strike from Deck VIII to feed the bridge.
          </div>
        ) : (
          <ul className="divide-y divide-[color:var(--gold-line)] max-h-[340px] overflow-y-auto">
            {strikes.map((s, idx) => {
              const Icon = ICON_FOR[s.hull_type] || Activity;
              const color = COLOR_FOR[s.hull_type] || "#dcb95f";
              const risk = s.summary?.risk_score;
              return (
                <li
                  key={s.id}
                  className="flex items-center gap-4 px-5 py-3"
                  style={{ borderLeft: `2px solid ${color}` }}
                  data-testid={`strike-${s.id.slice(0,6)}`}
                >
                  <Icon size={14} style={{ color }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span
                        className="font-mono text-[9px] tracking-[0.22em] uppercase px-1.5 py-px border"
                        style={{ color, borderColor: `${color}55` }}
                      >
                        {s.hull_type}
                      </span>
                      <span className="font-display text-[13px] tracking-[0.06em] text-gold-100 truncate">
                        {s.label}
                      </span>
                      {typeof risk === "number" && (
                        <span
                          className="font-mono text-[10px] tracking-[0.12em] px-1.5"
                          style={{
                            color: risk >= 60 ? "#ff7a6e" : risk >= 30 ? "#f5a73a" : "#6cc88f",
                          }}
                        >
                          risk {risk}
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-[10px] text-ivory-3 mt-0.5 tracking-[0.08em]">
                      {s.wayfinder_id} · {s.operator_email}
                      {s.summary?.host && ` · ${s.summary.host}`}
                      {s.summary?.hostname && ` · ${s.summary.hostname}`}
                      {s.summary?.provider && ` · ${s.summary.provider}`}
                    </div>
                  </div>
                  <TimeAgo iso={s.ingested_at} />
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Panel>
  );
};

export default LiveStrikeFeed;
